import { CanvasData } from "@/components/canvas/types/canvasTypes";
import { renderImageFromCanvasData } from "@/components/canvas/utils/renderUtils";
import { formatToUTC } from "@/lib/dateUtils";
import { showSuccess, showApiError } from "@/lib/toast";
import { Template } from "@/lib/types";
import { FormSchemaType } from "@/pages/content/CreateFromTemplateContentPropsForm";
import { updateContent, uploadContents } from "@/sideEffects/api";
import { selectActiveFirm, selectSelectedFolderGuid } from "@/store/appSelectors";
import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { useSelector } from "react-redux";

interface Props {
  newCanvasData?: CanvasData;
  template?: Template;
  onUploadSuccess: () => void;
  contentProps: FormSchemaType;
  contentGuid?: string;
}

export const useGenerateAndUpload = ({
  newCanvasData,
  template,
  onUploadSuccess,
  contentProps,
  contentGuid,
}: Props) => {
  const activeFirm = useSelector(selectActiveFirm);
  const selectedFolderGuid = useSelector(selectSelectedFolderGuid);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      const fileExtension = file.name.split(".").pop();
      return uploadContents({
        firmGuid: String(activeFirm?.firmGuid),
        isCustom: false,
        files: [file],
        folderGuid: selectedFolderGuid,
        name: `${contentProps.name}.${fileExtension}`,
        screenSizeId: contentProps.screenSizeId,
        startDate: formatToUTC(contentProps.startDate),
        endDate: formatToUTC(contentProps.endDate),
        duration: contentProps.duration,
      });
    },
    onSuccess: () => {
      onUploadSuccess();
      showSuccess(t("common.createdSuccess"));
    },
    onError: showApiError,
  });

  const generateAndUpload = async () => {
    if (!template) return;
    if (!newCanvasData) return;
    const dataUrl = await renderImageFromCanvasData(newCanvasData, { multiplier: 1 });
    if (!dataUrl) return;

    // Convert data URL to File object
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], `ignored.png`, {
      type: "image/png",
    });

    // upload the file
    uploadMutation.mutate(file);
  };

  const updateMutation = useMutation({
    mutationFn: (file: File) => {
      const fileExtension = file.name.split(".").pop();
      return updateContent({
        firmGuid: String(activeFirm?.firmGuid),
        files: [file],
        name: `${contentProps.name}.${fileExtension}`,
        contentGuid: String(contentGuid),
        templateLayoutConfig: JSON.stringify(template || {}),
      });
    },
    onSuccess: () => {
      onUploadSuccess();
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const generateAndUpdate = async () => {
    if (!template) return;
    if (!newCanvasData) return;
    const dataUrl = await renderImageFromCanvasData(newCanvasData, { multiplier: 1 });
    if (!dataUrl) return;

    // Convert data URL to File object
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], `ignored.png`, {
      type: "image/png",
    });

    // upload the file
    updateMutation.mutate(file);
  };

  return {
    generateAndUpload,
    generateAndUpdate,
    isUploading: uploadMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
};
